import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Stack, FormControl, InputLabel, Select, MenuItem,
  Grid, Typography, Alert, Chip, Box,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import AddIcon from "@mui/icons-material/Add";
import {
  convertEmailToLead, linkEmailToLead, fetchLeads,
  type LeadThread,
} from "../LeadSystem/leadApi";

const NAVY = "#0d1b2a";

const EVENT_TYPES = [
  "wedding", "corporate", "birthday", "engagement", "mehndi",
  "charity_gala", "convocation", "baby_shower", "anniversary",
  "retirement", "holiday_party", "other",
];

interface Props {
  open: boolean;
  onClose: () => void;
  onConverted: () => void;
  emailThreadId: number;
  prefillName?: string;
  prefillEmail?: string;
}

export default function ConvertToLeadModal({
  open, onClose, onConverted,
  emailThreadId, prefillName, prefillEmail,
}: Props) {
  const [mode, setMode] = useState<"convert" | "link">("convert");
  const [existingLeads, setExistingLeads] = useState<LeadThread[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<number | "">("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    event_type: "",
    event_date: "",
    guest_count: "",
    budget_value: "",
  });

  useEffect(() => {
    if (open) {
      setForm({
        name: prefillName || "",
        email: prefillEmail || "",
        phone: "", organization: "",
        event_type: "", event_date: "",
        guest_count: "", budget_value: "",
      });
      setMode("convert");
      setSelectedLeadId("");
      // Fetch existing leads for linking
      fetchLeads({ per_page: 50 }).then(setExistingLeads).catch(() => {});
    }
  }, [open, prefillName, prefillEmail]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (mode === "convert") {
        await convertEmailToLead({
          email_thread_id: emailThreadId,
          name: form.name,
          email: form.email || undefined,
          phone: form.phone || undefined,
          organization: form.organization || undefined,
          event_type: form.event_type || undefined,
          event_date: form.event_date || undefined,
          guest_count: form.guest_count ? parseInt(form.guest_count) : undefined,
          budget_value: form.budget_value ? parseFloat(form.budget_value) : undefined,
        });
      } else if (selectedLeadId) {
        await linkEmailToLead(emailThreadId, Number(selectedLeadId));
      }
      onConverted();
    } catch {}
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: NAVY, color: "#fff", fontWeight: 700 }}>
        Link to Lead System
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {/* Mode toggle */}
        <Stack direction="row" spacing={1} sx={{ mb: 2, pt: 1 }}>
          <Chip
            icon={<AddIcon />}
            label="Create New Lead"
            onClick={() => setMode("convert")}
            sx={{
              bgcolor: mode === "convert" ? NAVY : "#f1f5f9",
              color: mode === "convert" ? "#fff" : "#475569",
              fontWeight: 600,
              "&:hover": { opacity: 0.9 },
            }}
          />
          <Chip
            icon={<LinkIcon />}
            label="Link to Existing"
            onClick={() => setMode("link")}
            sx={{
              bgcolor: mode === "link" ? NAVY : "#f1f5f9",
              color: mode === "link" ? "#fff" : "#475569",
              fontWeight: 600,
              "&:hover": { opacity: 0.9 },
            }}
          />
        </Stack>

        {mode === "convert" ? (
          <Stack spacing={2}>
            <TextField label="Name *" size="small" fullWidth value={form.name} onChange={handleChange("name")} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Email" size="small" fullWidth value={form.email} onChange={handleChange("email")} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Phone" size="small" fullWidth value={form.phone} onChange={handleChange("phone")} />
              </Grid>
            </Grid>
            <TextField label="Organization" size="small" fullWidth value={form.organization} onChange={handleChange("organization")} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Event Type</InputLabel>
                  <Select value={form.event_type} label="Event Type" onChange={(e) => setForm((p) => ({ ...p, event_type: e.target.value }))}>
                    {EVENT_TYPES.map((t) => (
                      <MenuItem key={t} value={t} sx={{ textTransform: "capitalize" }}>
                        {t.replace(/_/g, " ")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField label="Event Date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.event_date} onChange={handleChange("event_date")} />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Guest Count" type="number" size="small" fullWidth value={form.guest_count} onChange={handleChange("guest_count")} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Budget ($)" type="number" size="small" fullWidth value={form.budget_value} onChange={handleChange("budget_value")} />
              </Grid>
            </Grid>
          </Stack>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Link this email thread to an existing lead
            </Alert>
            <FormControl size="small" fullWidth>
              <InputLabel>Select Lead</InputLabel>
              <Select
                value={selectedLeadId}
                label="Select Lead"
                onChange={(e) => setSelectedLeadId(e.target.value as number)}
              >
                {existingLeads.map((lead) => (
                  <MenuItem key={lead.id} value={lead.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {lead.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                        {lead.email || ""} &middot; {lead.event_type?.replace(/_/g, " ") || ""}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            saving ||
            (mode === "convert" && !form.name.trim()) ||
            (mode === "link" && !selectedLeadId)
          }
          sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1b2e44" } }}
        >
          {saving ? "Saving..." : mode === "convert" ? "Convert to Lead" : "Link to Lead"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
